import * as React from "react";
import ActionButton, { ActionButtonType, IconPosition } from "components/Button/ActionButton";
import * as colors from "theme/colors";
import { PopoverProps, Origin } from "components/Popover";
import { makeStyles } from "@material-ui/styles";
import { Theme } from "theme";
import Menu, { MenuProps } from "@material-ui/core/Menu";
import MenuItem, { MenuItemProps as MuiMenuItemProps } from "@material-ui/core/MenuItem";
import callAll from "utils/callAll";
import Navigate, { NavigateRenderProps } from "components/Navigation/Navigate";

interface DropdownButtonProps {
    className?: string;
    open: boolean;
    onClose: () => void;
    label: string;
    icon?: React.ReactElement<any>;
    type?: DropdownButtonType;
    children: (props: RenderMenuProps) => React.ReactNode;
    onClick: React.MouseEventHandler;
}

type MenuItemProps = Pick<MuiMenuItemProps, "classes" | "onClick">;

interface RenderMenuProps {
    open: boolean;
    navigation: NavigateRenderProps;
    getLinkClass: () => string;
    getMenuItemProps: (props?: Partial<MenuItemProps>) => MenuItemProps;
    getMenuProps: (props?: Partial<MenuProps>) => MenuProps;
}

export const DropdownButtonMenu = Menu;
type DropdownButtonType = ActionButtonType;

export const DropdownButtonMenuItem = MenuItem;

interface ActionPopoverProps extends PopoverProps {
    type: ActionButtonType;
}

type ActionButtonTypeProp = Pick<ActionPopoverProps, "type">;

const useActionColors = makeStyles((theme: Theme) => ({
    root: ({ type }: ActionButtonTypeProp) => {
        switch (type) {
            case ActionButtonType.Primary:
                return { color: colors.white, backgroundColor: colors.success };
            case ActionButtonType.Secondary:
                return { color: colors.white, backgroundColor: colors.secondaryBackground };
            case ActionButtonType.Ternary:
                return {};
            case ActionButtonType.Save:
                return { color: colors.white, backgroundColor: colors.success };
            case ActionButtonType.Delete:
                return { color: colors.white, backgroundColor: colors.danger };
            case ActionButtonType.Category:
                return { backgroundColor: colors.primaryLightest };
            case ActionButtonType.CreateRelease:
                return { color: colors.white, backgroundColor: colors.secondaryDark };
        }
        return {};
    },
}));

const useLinkStyles = makeStyles((theme: Theme) => ({
    root: (props: ActionButtonTypeProp) => ({
        textDecoration: "none",
        color: "inherit",
        "&:hover": {
            color: "inherit",
        },
        "&:visited": {
            color: "inherit",
        },
    }),
}));

const getDefaultAnchorPosition = (): { transformOrigin: Origin; anchorOrigin: Origin } => {
    return {
        transformOrigin: { horizontal: "right", vertical: "top" },
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
    };
};

export const DropdownButton: React.FC<DropdownButtonProps> = React.forwardRef((props, ref) => {
    const [anchor, setAnchor] = React.useState<any>(null);
    const linkStyles = useLinkStyles({ type: props.type });

    const handleButtonTouchTab = (event: React.SyntheticEvent<any>) => {
        event.preventDefault();
        setAnchor(event.currentTarget);
    };

    const getMenuItemProps = (menuItemProps: MenuItemProps = {}): MenuItemProps => {
        const { onClick, ...rest } = menuItemProps;

        return {
            onClick: callAll(onClick),
            ...rest,
        };
    };

    const handleBackdropClick: React.MouseEventHandler = e => {
        props.onClose();
    };

    const getMenuProps = ({ onClose, onBackdropClick, ...rest }: Partial<MenuProps> = {}): MenuProps => {
        return {
            open: props.open,
            onBackdropClick: callAll(handleBackdropClick, onBackdropClick),
            ...getDefaultAnchorPosition(),
            ...(anchor ? { anchorEl: anchor } : {}),
            onClose: callAll(onClose),
            getContentAnchorEl: null,
            ...rest,
        };
    };

    const getLinkClass = () => linkStyles.root;

    const { icon = <i className="fa fa-caret-down" style={{ color: colors.white }} /> } = props;

    return (
        <Navigate
            render={navigateRenderProps => (
                <React.Fragment>
                    <ActionButton innerRef={ref} type={props.type} icon={icon} iconPosition={IconPosition.Right} label={props.label} onClick={callAll(handleButtonTouchTab, props.onClick)} />
                    {props.children({
                        navigation: navigateRenderProps,
                        open: props.open,
                        getMenuItemProps,
                        getMenuProps,
                        getLinkClass,
                    })}
                </React.Fragment>
            )}
        />
    );
});

export default DropdownButton;
